import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { FaRupeeSign, FaChartLine, FaCalendar, FaPercent, FaInfoCircle, FaQuestionCircle } from 'react-icons/fa';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const EPFCalculator = () => {
  const [basicSalary, setBasicSalary] = useState('');
  const [yearsOfService, setYearsOfService] = useState('');
  const [employeeContribution, setEmployeeContribution] = useState('');
  const [employerContribution, setEmployerContribution] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [totalBalance, setTotalBalance] = useState('');
  const [monthlyBreakdown, setMonthlyBreakdown] = useState([]);

  const calculateEPF = () => {
    if (!basicSalary || !yearsOfService || !employeeContribution || !employerContribution || !interestRate) return;

    const P = parseFloat(basicSalary);
    const years = parseFloat(yearsOfService);
    const empContri = parseFloat(employeeContribution) / 100;
    const empContriAmount = P * empContri;
    const empContriTotal = empContriAmount * years * 12;

    const employerContri = parseFloat(employerContribution) / 100;
    const employerContriAmount = P * employerContri;
    const employerContriTotal = employerContriAmount * years * 12;

    const r = parseFloat(interestRate) / 100;
    const n = years * 12;

    // Calculate total EPF balance using compound interest
    const totalBalanceValue = (empContriTotal + employerContriTotal) * Math.pow(1 + r, years);
    setTotalBalance(totalBalanceValue.toFixed(2));

    // Calculate monthly breakdown
    const breakdown = [];
    let currentBalance = 0;
    for (let i = 0; i < n; i++) {
      const monthlyContribution = empContriAmount + employerContriAmount;
      currentBalance += monthlyContribution;
      currentBalance *= (1 + r/12);
      breakdown.push({
        month: i + 1,
        contribution: monthlyContribution,
        balance: currentBalance
      });
    }
    setMonthlyBreakdown(breakdown);
  };

  const chartData = {
    labels: monthlyBreakdown.map(item => `Month ${item.month}`),
    datasets: [
      {
        label: 'Monthly Contribution',
        data: monthlyBreakdown.map(item => item.contribution),
        borderColor: '#4ecdc4',
        tension: 0.1,
        fill: true,
        backgroundColor: 'rgba(78, 205, 196, 0.1)'
      },
      {
        label: 'Total Balance',
        data: monthlyBreakdown.map(item => item.balance),
        borderColor: '#ff6b6b',
        tension: 0.1,
        fill: true,
        backgroundColor: 'rgba(255, 107, 107, 0.1)'
      }
    ],
  };

  return (
    <div className="bg-gradient-to-br from-blue-50 to-purple-50 min-h-screen p-6">
      <Helmet>
        <title>EPF Calculator</title>
        <meta name="description" content="Calculate your EPF contributions, interest, and total balance with our easy-to-use calculator." />
        <meta property="og:title" content="EPF Calculator" />
        <meta property="og:description" content="Calculate your EPF contributions, interest, and total balance with our easy-to-use calculator." />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl font-bold text-gray-800">
            <FaChartLine className="inline-block mr-2 text-blue-500" />
            EPF Calculator
          </h2>
          <div className="flex items-center space-x-4">
            <button className="text-blue-500 hover:text-blue-600">
              <FaInfoCircle className="w-6 h-6" />
            </button>
            <button className="text-blue-500 hover:text-blue-600">
              <FaQuestionCircle className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-blue-50 p-4 rounded-xl">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Basic Salary (₹)
            </label>
            <div className="relative">
              <FaRupeeSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="number"
                value={basicSalary}
                onChange={(e) => setBasicSalary(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                placeholder="Enter basic salary"
              />
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-xl">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Years of Service
            </label>
            <div className="relative">
              <FaCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="number"
                value={yearsOfService}
                onChange={(e) => setYearsOfService(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                placeholder="Enter years of service"
              />
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-xl">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Employee Contribution (%)
            </label>
            <div className="relative">
              <FaPercent className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="number"
                value={employeeContribution}
                onChange={(e) => setEmployeeContribution(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                placeholder="Enter employee contribution"
              />
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-xl">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Employer Contribution (%)
            </label>
            <div className="relative">
              <FaPercent className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="number"
                value={employerContribution}
                onChange={(e) => setEmployerContribution(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                placeholder="Enter employer contribution"
              />
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-xl">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Interest Rate (% p.a.)
            </label>
            <div className="relative">
              <FaPercent className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="number"
                value={interestRate}
                onChange={(e) => setInterestRate(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                placeholder="Enter interest rate"
              />
            </div>
          </div>
        </div>

        <button
          onClick={calculateEPF}
          className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-green-500 font-bold py-3 px-6 rounded-xl hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:ring-offset-2 transition-all duration-200"
        >
          Calculate EPF
        </button>

        {totalBalance && (
          <div className="mt-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-blue-100 p-6 rounded-xl text-center">
                <h3 className="text-2xl font-bold text-blue-600">
                  <FaRupeeSign className="inline-block mr-1" />
                  {totalBalance}
                </h3>
                <p className="text-gray-600">Total EPF Balance</p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="text-xl font-bold mb-4">EPF Growth</h3>
              <div className="w-full h-[500px]">
                <Line data={chartData} options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'top',
                      labels: {
                        font: {
                          size: 16
                        },
                        padding: 20
                      }
                    },
                    title: {
                      display: true,
                      text: 'EPF Growth Over Time',
                      font: {
                        size: 18
                      },
                      padding: 20
                    },
                    tooltip: {
                      callbacks: {
                        label: function(context) {
                          const label = context.dataset.label;
                          const value = context.parsed.y;
                          return `${label}: ₹${value.toLocaleString()}`;
                        }
                      },
                      padding: 10,
                      titleFont: {
                        size: 14
                      },
                      bodyFont: {
                        size: 14
                      }
                    }
                  },
                  scales: {
                    x: {
                      title: {
                        display: true,
                        text: 'Month',
                        font: {
                          size: 16
                        },
                        padding: 20
                      },
                      ticks: {
                        font: {
                          size: 14
                        },
                        autoSkip: true,
                        maxTicksLimit: 20,
                        padding: 10
                      },
                      grid: {
                        display: false
                      }
                    },
                    y: {
                      beginAtZero: true,
                      title: {
                        display: true,
                        text: 'Amount (₹)',
                        font: {
                          size: 16
                        },
                        padding: 20
                      },
                      ticks: {
                        callback: function(value) {
                          return '₹' + value.toLocaleString();
                        },
                        font: {
                          size: 14
                        },
                        padding: 10
                      },
                      grid: {
                        drawBorder: false,
                        tickColor: 'rgba(0, 0, 0, 0.1)'
                      }
                    }
                  },
                  animation: {
                    duration: 1500,
                    easing: 'easeInOutQuad',
                    delay: 200
                  }
                }} />
              </div>
            </div>
          </div>
        )}

        <div className="mt-12">
          <h3 className="text-2xl font-bold mb-6">Features & Benefits</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-blue-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold mb-2">Accurate Calculations</h4>
              <p className="text-gray-600">Get precise EPF calculations based on your salary, contributions, and tenure.</p>
            </div>
            <div className="bg-blue-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold mb-2">Detailed Breakdown</h4>
              <p className="text-gray-600">See how your EPF balance grows over time with monthly contributions and interest.</p>
            </div>
            <div className="bg-blue-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold mb-2">Visual Representation</h4>
              <p className="text-gray-600">View an interactive chart showing your EPF growth over time.</p>
            </div>
            <div className="bg-blue-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold mb-2">Tax Benefits</h4>
              <p className="text-gray-600">Understand the tax benefits of your EPF contributions.</p>
            </div>
          </div>
        </div>

        <div className="mt-12">
          <h3 className="text-2xl font-bold mb-6">How to Use</h3>
          <ol className="list-decimal list-inside space-y-4">
            <li>
              <div className="bg-blue-50 p-4 rounded-xl">
                <h4 className="text-xl font-semibold mb-2">Enter Details</h4>
                <p className="text-gray-600">Input your basic salary, years of service, and contribution percentages.</p>
              </div>
            </li>
            <li>
              <div className="bg-blue-50 p-4 rounded-xl">
                <h4 className="text-xl font-semibold mb-2">Calculate EPF</h4>
                <p className="text-gray-600">Click the "Calculate EPF" button to see your total EPF balance.</p>
              </div>
            </li>
            <li>
              <div className="bg-blue-50 p-4 rounded-xl">
                <h4 className="text-xl font-semibold mb-2">View Growth</h4>
                <p className="text-gray-600">Check the growth chart to visualize how your EPF balance will grow over time.</p>
              </div>
            </li>
          </ol>
        </div>

        <div className="mt-12">
          <h3 className="text-2xl font-bold mb-6">FAQ</h3>
          <div className="space-y-6">
            <div className="bg-blue-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold mb-2">What is EPF?</h4>
              <p className="text-gray-600">EPF (Employees' Provident Fund) is a mandatory retirement savings scheme for salaried employees in India.</p>
            </div>
            <div className="bg-blue-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold mb-2">How is EPF calculated?</h4>
              <p className="text-gray-600">EPF is calculated based on your basic salary and includes both employee and employer contributions, plus interest earned.</p>
            </div>
            <div className="bg-blue-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold mb-2">What are the benefits of EPF?</h4>
              <p className="text-gray-600">EPF offers tax benefits, retirement savings, and interest income. The contributions are tax-free up to certain limits.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EPFCalculator;
